import { Injectable } from '@angular/core';
import { BehaviorSubject, finalize, forkJoin, take } from 'rxjs';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { ApiOperationHelper } from '../helpers/api-operation.helper';
import {
  ActiveIndicatorItemRequestDto,
  ActiveIndicatorResponseDto,
  ActiveIndicatorService,
} from 'src/generated-sources/openapi/alert-service/active-indicator';
import {
  IndicatorDto,
  ParentPlanIndicatorResponseDto,
  PlanIndicatorResponseDto,
  PlanIndicatorService,
} from 'src/generated-sources/openapi/configuration-service/plan-indicator';
import { RegistryTypeDto } from 'src/generated-sources/openapi/wallet-service/wallet';
import { IndicatorDataService } from 'src/app/services/data/indicator-data.service';

type RelationshipLevel = ActiveIndicatorResponseDto.RelationshipLevelEnum;
const RelationshipLevel = ActiveIndicatorResponseDto.RelationshipLevelEnum;
const VIRTUAL_GROUP_SOCIOS_ID = 'virtual-socios';
const VIRTUAL_GROUP_OUTRAS_PARTICIPACOES_ID = 'virtual-outras-participacoes';

export type IndicatorWithStatus = {
  id: string;
  name: string;
  dataFabricKey?: string;
  group?: string;
  activeIndicatorId: string;
  active: boolean;
  relationshipLevel: RelationshipLevel;
  fromNonRootParent?: boolean;
  control?: {
    deletedAt?: string;
    deletedBy?: string;
  };
};

export interface SubGroup {
  name: string;
  active: boolean;
  indicators: IndicatorWithStatus[];
}

export interface GroupWithSubGroups {
  id: string;
  name: string;
  subgroups: SubGroup[];
}

@Injectable()
export class WalletAttributesAlertConfigService {
  private readonly groupedIndicators$ = new BehaviorSubject<GroupWithSubGroups[]>([]);
  private readonly actionIsLoading$ = new BehaviorSubject<boolean>(false);
  private readonly isLoadingIndicatorsList$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly planIndicatorService: PlanIndicatorService,
    private readonly activeIndicatorService: ActiveIndicatorService,
    private readonly snackbarService: SnackbarService,
    private readonly indicatorDataService: IndicatorDataService,
  ) {}

  fetchIndicators(walletId: string, planId: string, walletType: RegistryTypeDto): void {
    this.isLoadingIndicatorsList$.next(true);

    forkJoin({
      grouped: this.planIndicatorService.findByPlanIdGrouped({ planId }).pipe(take(1)),
      active: this.activeIndicatorService
        .listAllActiveIndicatorByWallet({ id: walletId })
        .pipe(take(1)),
    })
      .pipe(finalize(() => this.isLoadingIndicatorsList$.next(false)))
      .subscribe({
        next: ({ grouped, active }) => {
          this.groupIndicators(grouped.items, active.items ?? [], walletType);
        },
        error: (error) => {
          this.groupedIndicators$.next([]);
          this.snackbarService.error(
            $localize`:@@js_getIndicatorsListError:Error al intentar obtener la lista de indicadores`,
          );
          console.error('Erro ao buscar indicadores:', error);
        },
      });
  }

  private groupIndicators(
    parentIndicators: ParentPlanIndicatorResponseDto[],
    activeIndicators: ActiveIndicatorResponseDto[],
    walletType: RegistryTypeDto,
  ): void {
    const activeIndicatorMap = this.buildActiveIndicatorMap(activeIndicators);

    const groupsWithSubgroups: GroupWithSubGroups[] = this.buildRegularGroups(
      parentIndicators,
      activeIndicatorMap,
    );

    if (walletType === RegistryTypeDto.Company && this.hasVirtualGroups(parentIndicators)) {
      this.appendVirtualPjGroups(parentIndicators, activeIndicatorMap, groupsWithSubgroups);
    }

    this.finalizeSubgroupStatus(groupsWithSubgroups);

    this.groupedIndicators$.next(this.buildFinalOrderedList(groupsWithSubgroups));
  }

  private buildActiveIndicatorMap(
    activeIndicators: ActiveIndicatorResponseDto[],
  ): Map<string, ActiveIndicatorResponseDto> {
    return new Map(
      activeIndicators.map((ai) => [
        this.activeIndicatorKey(ai.indicatorId, ai.relationshipLevel ?? RelationshipLevel.Root),
        ai,
      ]),
    );
  }

  private buildRegularGroups(
    parentIndicators: ParentPlanIndicatorResponseDto[],
    activeIndicatorMap: Map<string, ActiveIndicatorResponseDto>,
  ): GroupWithSubGroups[] {
    return parentIndicators.reduce<GroupWithSubGroups[]>((acc, parentIndicator) => {
      const levels = parentIndicator.supportedRelationshipLevels ?? ['ROOT'];
      if (!levels.includes('ROOT')) return acc;

      const group: GroupWithSubGroups = {
        id: parentIndicator.id,
        name: parentIndicator.name,
        subgroups: [],
      };

      this.populateSubgroups(
        group,
        parentIndicator.planIndicators,
        activeIndicatorMap,
        RelationshipLevel.Root,
      );

      if (group.subgroups.length > 0) {
        acc.push(group);
      }

      return acc;
    }, []);
  }

  private hasVirtualGroups(parentIndicators: ParentPlanIndicatorResponseDto[]): boolean {
    return parentIndicators.some((pi) => {
      const levels = pi.supportedRelationshipLevels ?? ['ROOT'];
      return !levels.includes('ROOT');
    });
  }

  private appendVirtualPjGroups(
    parentIndicators: ParentPlanIndicatorResponseDto[],
    activeIndicatorMap: Map<string, ActiveIndicatorResponseDto>,
    groupsWithSubgroups: GroupWithSubGroups[],
  ): void {
    const sociosGroup: GroupWithSubGroups = {
      id: VIRTUAL_GROUP_SOCIOS_ID,
      name: $localize`:@@walletAlertVirtualGroupSocios:Socios`,
      subgroups: [],
    };

    const outrasParticipacoesGroup: GroupWithSubGroups = {
      id: VIRTUAL_GROUP_OUTRAS_PARTICIPACOES_ID,
      name: $localize`:@@walletAlertVirtualGroupOutrasParticipacoes:Otras Participaciones`,
      subgroups: [],
    };

    for (const parentIndicator of parentIndicators) {
      const fromNonRootParent = !(parentIndicator.supportedRelationshipLevels ?? []).includes(
        'ROOT',
      );
      this.populateSubgroups(
        sociosGroup,
        parentIndicator.planIndicators,
        activeIndicatorMap,
        RelationshipLevel.L1,
        fromNonRootParent,
      );
      this.populateSubgroups(
        outrasParticipacoesGroup,
        parentIndicator.planIndicators,
        activeIndicatorMap,
        RelationshipLevel.L2,
        fromNonRootParent,
      );
    }

    if (sociosGroup.subgroups.length > 0) groupsWithSubgroups.push(sociosGroup);
    if (outrasParticipacoesGroup.subgroups.length > 0)
      groupsWithSubgroups.push(outrasParticipacoesGroup);
  }

  private populateSubgroups(
    group: GroupWithSubGroups,
    planIndicators: PlanIndicatorResponseDto[],
    activeIndicatorMap: Map<string, ActiveIndicatorResponseDto>,
    relationshipLevel: RelationshipLevel,
    fromNonRootParent: boolean = false,
  ): void {
    if (!planIndicators?.length) return;

    for (const planIndicator of planIndicators) {
      const indicator: IndicatorDto = planIndicator.indicator;

      if (relationshipLevel !== RelationshipLevel.Root) {
        const supported: string[] = indicator.supportedRelationshipLevels ?? [];
        if (!supported.includes(relationshipLevel)) continue;
      }

      const activeIndicator = activeIndicatorMap.get(
        this.activeIndicatorKey(indicator.id, relationshipLevel),
      );

      const indicatorWithStatus: IndicatorWithStatus = {
        id: indicator.id,
        name: indicator.name,
        dataFabricKey: indicator.dataFabricKey,
        group: indicator.group,
        activeIndicatorId: activeIndicator?.id,
        active: !!(activeIndicator?.id && !activeIndicator?.control?.deletedAt),
        relationshipLevel,
        fromNonRootParent,
      };

      let subgroup = group.subgroups.find((sg) => sg.name === indicator.group);

      if (!subgroup) {
        subgroup = { name: indicator.group, active: false, indicators: [] };
        group.subgroups.push(subgroup);
      }

      subgroup.indicators.push(indicatorWithStatus);
    }
  }

  private finalizeSubgroupStatus(groups: GroupWithSubGroups[]): void {
    const orderMap = this.indicatorDataService.getOrderMap();
    const isVirtualGroup = (g: GroupWithSubGroups) =>
      g.id === VIRTUAL_GROUP_SOCIOS_ID || g.id === VIRTUAL_GROUP_OUTRAS_PARTICIPACOES_ID;

    for (const group of groups) {
      for (const subgroup of group.subgroups) {
        if (isVirtualGroup(group)) {
          const nonRoot = this.indicatorDataService.sortByDisplayOrder(
            subgroup.indicators.filter((i) => i.fromNonRootParent),
          );
          const root = this.indicatorDataService.sortByDisplayOrder(
            subgroup.indicators.filter((i) => !i.fromNonRootParent),
          );
          subgroup.indicators = [...nonRoot, ...root];
        } else {
          subgroup.indicators = this.indicatorDataService.sortByDisplayOrder(subgroup.indicators);
        }

        subgroup.active =
          subgroup.indicators.length > 0 &&
          subgroup.indicators.every((indicator) => indicator.active);
      }

      group.subgroups = [...group.subgroups].sort((a, b) => {
        if (isVirtualGroup(group)) {
          const aHasNonRoot = a.indicators.some((i) => i.fromNonRootParent);
          const bHasNonRoot = b.indicators.some((i) => i.fromNonRootParent);
          if (aHasNonRoot !== bHasNonRoot) return aHasNonRoot ? -1 : 1;
        }
        const orderA = Math.min(
          ...a.indicators.map((i) => orderMap.get(i.id) ?? Number.MAX_SAFE_INTEGER),
        );
        const orderB = Math.min(
          ...b.indicators.map((i) => orderMap.get(i.id) ?? Number.MAX_SAFE_INTEGER),
        );
        return orderA - orderB;
      });
    }
  }

  private buildFinalOrderedList(groups: GroupWithSubGroups[]): GroupWithSubGroups[] {
    const regularGroups = groups.filter(
      (g) => g.id !== VIRTUAL_GROUP_SOCIOS_ID && g.id !== VIRTUAL_GROUP_OUTRAS_PARTICIPACOES_ID,
    );

    const finalList = this.indicatorDataService.sortByDisplayOrder(regularGroups, (g) => g.id);

    const socios = groups.find((g) => g.id === VIRTUAL_GROUP_SOCIOS_ID);
    const outrasParticipacoes = groups.find((g) => g.id === VIRTUAL_GROUP_OUTRAS_PARTICIPACOES_ID);

    if (socios) finalList.push(socios);
    if (outrasParticipacoes) finalList.push(outrasParticipacoes);

    return finalList;
  }

  toggleSubgroupAlertStatus(subgroup: SubGroup, walletId: string): void {
    this.actionIsLoading$.next(true);

    const indicatorsWithoutActiveId = subgroup.indicators.filter((i) => !i.activeIndicatorId);
    const indicatorsWithActiveId = subgroup.indicators.filter((i) => i.activeIndicatorId);

    if (indicatorsWithoutActiveId.length > 0) {
      this.createActiveIndicatorsForSubgroup(subgroup, walletId);
      return;
    }

    if (!subgroup.active) {
      this.disableActiveIndicatorsForSubgroup(subgroup, indicatorsWithActiveId);
    } else {
      this.enableActiveIndicatorsForSubgroup(subgroup, indicatorsWithActiveId);
    }
  }

  private createActiveIndicatorsForSubgroup(subgroup: SubGroup, walletId: string): void {
    const indicators: ActiveIndicatorItemRequestDto[] = subgroup.indicators.map((indicator) => ({
      indicatorId: indicator.id,
      relationshipLevel: indicator.relationshipLevel,
    }));

    ApiOperationHelper.executeWithStateRollback(
      {
        operation: () =>
          this.activeIndicatorService.createActiveIndicator({
            id: walletId,
            createActiveIndicatorRequestDto: { indicators },
          }),
        onSuccess: (response) =>
          this.updateSubgroupStatusLocally(subgroup, response.activeIndicators, 'CREATE'),
        rollbackAction: () => {
          subgroup.active = !subgroup.active;
        },
        errorMessage: $localize`:@@ErrorTryingToEditStatus:Error al intentar cambiar el status`,
        loadingState: this.actionIsLoading$,
      },
      this.snackbarService,
    );
  }

  private enableActiveIndicatorsForSubgroup(
    subgroup: SubGroup,
    indicators: IndicatorWithStatus[],
  ): void {
    const ids = indicators.map((i) => i.activeIndicatorId);

    ApiOperationHelper.executeWithStateRollback(
      {
        operation: () =>
          this.activeIndicatorService.enableActiveIndicator({
            activeIndicatorRequestDto: { ids },
          }),
        onSuccess: (response) =>
          this.updateSubgroupStatusLocally(subgroup, response.activeIndicators, 'ENABLE'),
        rollbackAction: () => {
          subgroup.active = !subgroup.active;
        },
        errorMessage: $localize`:@@ErrorTryingToEditStatus:Error al intentar cambiar el status`,
        loadingState: this.actionIsLoading$,
      },
      this.snackbarService,
    );
  }

  private disableActiveIndicatorsForSubgroup(
    subgroup: SubGroup,
    indicators: IndicatorWithStatus[],
  ): void {
    const ids = indicators.map((i) => i.activeIndicatorId);

    ApiOperationHelper.executeWithStateRollback(
      {
        operation: () =>
          this.activeIndicatorService.disableActiveIndicator({
            activeIndicatorRequestDto: { ids },
          }),
        onSuccess: (response) =>
          this.updateSubgroupStatusLocally(subgroup, response.activeIndicators, 'DISABLE'),
        rollbackAction: () => {
          subgroup.active = !subgroup.active;
        },
        errorMessage: $localize`:@@ErrorTryingToEditStatus:Error al intentar cambiar el status`,
        loadingState: this.actionIsLoading$,
      },
      this.snackbarService,
    );
  }

  private updateSubgroupStatusLocally(
    subgroup: SubGroup,
    items: ActiveIndicatorResponseDto[],
    action: 'CREATE' | 'ENABLE' | 'DISABLE'
  ): void {
    if (!items) {
      this.groupedIndicators$.next([...this.groupedIndicators$.getValue()]);
      return;
    }

    const responseMap = new Map(
      items.map((item) => [
        this.activeIndicatorKey(item.indicatorId, item.relationshipLevel ?? RelationshipLevel.Root),
        item,
      ]),
    );

    for (const indicator of subgroup.indicators) {
      const responseItem = responseMap.get(
        this.activeIndicatorKey(
          indicator.id,
          indicator.relationshipLevel ?? RelationshipLevel.Root,
        ),
      );

      if (responseItem) {
        indicator.activeIndicatorId = responseItem.id;
        indicator.control = {
          ...indicator.control,
          deletedAt: responseItem.control?.deletedAt,
          deletedBy: responseItem.control?.deletedBy,
        };
        indicator.active = action !== 'DISABLE';
      } else if (action === 'DISABLE') {
        indicator.active = false;
      }
    }

    subgroup.active = subgroup.indicators.every((indicator) => indicator.active);

    this.groupedIndicators$.next([...this.groupedIndicators$.getValue()]);
  }

  private activeIndicatorKey(indicatorId: string, level: RelationshipLevel): string {
    return `${indicatorId}-${level}`;
  }

  getGroupedIndicators$() {
    return this.groupedIndicators$.asObservable();
  }

  getActionIsLoading$() {
    return this.actionIsLoading$.asObservable();
  }

  getIsLoadingIndicatorsList$() {
    return this.isLoadingIndicatorsList$.asObservable();
  }
}
